import React, { useState, useEffect } from "react";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { db } from "../firebase";
import { SHOP } from "../shop";

// ─── Colour tokens ────────────────────────────────────────────────────────────

const BRAND = SHOP.accent;

// ─── Default shipping email texts (mirrors SHIPPING_EMAIL_DEFAULTS in functions) ──

const SHIPPING_EMAIL_DEFAULTS = {
  fromLocal: "versand",
  subject: "Deine Bestellung ist unterwegs! 📦 #{{ORDER}}",
  body:
    "Hallo {{NAME}},\n\n" +
    "deine Bestellung #{{ORDER}} ist gerade auf dem Weg zu dir!\n\n" +
    "Wir haben dein Paket soeben an DHL übergeben. " +
    "Du kannst deine Sendung jederzeit verfolgen:\n\n" +
    "{{TRACKING_LINK}}\n\n" +
    "Deine Sendungsnummer: {{TRACKING}}\n\n" +
    "Wir freuen uns, dass wir dir eine schöne Erinnerung gestalten " +
    "durften, und wünschen dir viel Freude mit deinem " +
    "personalisierten Geschenk.\n\n" +
    "Herzliche Grüße\n" +
    "Dein " + SHOP.name + "-Team",
  emailFooter: "© " + SHOP.name + " · " + SHOP.email,
};

// ─── HTML rendering helpers ───────────────────────────────────────────────────

const trackingButton = (trackingNumber) => {
  const url =
    "https://www.dhl.de/de/privatkunden/dhl-sendungsverfolgung.html" +
    `?piececode=${encodeURIComponent(trackingNumber)}`;
  return (
    `<p style="text-align:center;margin:20px 0;">` +
    `<a href="${url}" style="background:${BRAND};color:#ffffff;` +
    `padding:12px 28px;border-radius:6px;text-decoration:none;` +
    `font-weight:bold;font-size:15px;display:inline-block;">` +
    `Sendung verfolgen &#8594;</a></p>`
  );
};

const emailWrapper = (bodyHtml, footer) =>
  `<!DOCTYPE html><html><body style="margin:0;padding:0;background:#fdf7f4;` +
  `font-family:Arial,sans-serif;">` +
  `<table width="100%" cellpadding="0" cellspacing="0">` +
  `<tr><td align="center" style="padding:32px 16px;">` +
  `<table width="560" cellpadding="0" cellspacing="0" style="background:#ffffff;` +
  `border-radius:8px;box-shadow:0 1px 4px rgba(0,0,0,0.08);overflow:hidden;">` +
  `<tr><td style="background:${BRAND};padding:20px 32px;">` +
  `<span style="color:#ffffff;font-size:18px;font-weight:bold;">${SHOP.name}</span>` +
  `</td></tr>` +
  `<tr><td style="padding:32px;">${bodyHtml}</td></tr>` +
  `<tr><td style="padding:16px 32px;border-top:1px solid #e8d8d0;` +
  `font-size:12px;color:#a0aec0;text-align:center;">` +
  `${footer || `© ${SHOP.name} · ${SHOP.email}`}` +
  `</td></tr>` +
  `</table></td></tr></table></body></html>`;

const trackingInlineLink = (trackingNumber) => {
  const url =
    "https://www.dhl.de/de/privatkunden/dhl-sendungsverfolgung.html" +
    `?piececode=${encodeURIComponent(trackingNumber)}`;
  return (
    `<a href="${url}" style="color:${BRAND};font-weight:bold;">` +
    `Sendung verfolgen &#8594;</a>`
  );
};

const buildShippingEmailHtml = (bodyText, name, orderId, tracking, footer) => {
  const btn = trackingButton(tracking);
  const link = trackingInlineLink(tracking);
  const lines = bodyText
    .replace(/\{\{NAME\}\}/g, name || "Kunde")
    .replace(/\{\{ORDER\}\}/g, orderId || "")
    .replace(/\{\{TRACKING\}\}/g, tracking || "")
    .split("\n");
  const parts = lines.map((line) => {
    if (line.trim() === "{{TRACKING_LINK}}") return btn;
    if (line.trim() === "") return `<div style="height:10px;"></div>`;
    if (line.includes("{{TRACKING_LINK}}")) {
      const rendered = line.replace(/\{\{TRACKING_LINK\}\}/g, link);
      return (
        `<p style="color:#4a5568;line-height:1.5;margin:0 0 8px;">` +
        `${rendered}</p>`
      );
    }
    return `<p style="color:#4a5568;line-height:1.5;margin:0 0 8px;">${line}</p>`;
  });
  return emailWrapper(parts.join(""), footer);
};

// ─── Shared input/textarea styles ─────────────────────────────────────────────

const labelStyle = {
  display: "block", fontWeight: "600", fontSize: "0.78rem",
  color: "#4a5568", marginBottom: "0.3em",
  textTransform: "uppercase", letterSpacing: "0.04em",
};
const inputStyle = {
  width: "100%", boxSizing: "border-box",
  padding: "0.5em 0.75em", border: "1px solid #e8d8d0",
  borderRadius: "6px", fontSize: "0.875rem",
  color: "#2d3748", background: "#fff",
  fontFamily: "inherit", marginBottom: "1.1em",
};
const taStyle = { ...inputStyle, resize: "vertical" };

// ─── Component ────────────────────────────────────────────────────────────────

const ShippingEmailPreview = () => {
  const [editMode,    setEditMode]    = useState(false);
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);
  const [form,        setForm]        = useState(null);
  const [testEmail,   setTestEmail]   = useState("tobias.boldt@gmail.com");
  const [testSending, setTestSending] = useState(false);
  const [testResult,  setTestResult]  = useState(null); // "ok" | "error"

  // Sample data for preview
  const [firstName,      setFirstName]      = useState("Maria");
  const [orderNumber,    setOrderNumber]    = useState("4007861969");
  const [trackingNumber, setTrackingNumber] = useState("00340434334442708607");

  useEffect(() => {
    const load = async () => {
      const snap = await getDoc(doc(db, "config", "shippingEmailTexts"));
      setForm(snap.exists()
        ? { ...SHIPPING_EMAIL_DEFAULTS, ...snap.data() }
        : { ...SHIPPING_EMAIL_DEFAULTS });
    };
    load();
  }, []);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleSave = async () => {
    setSaving(true);
    try {
      await setDoc(doc(db, "config", "shippingEmailTexts"), form);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
      setEditMode(false);
    } catch (err) {
      alert("Speichern fehlgeschlagen: " + err.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <p style={{ color: "#718096", fontStyle: "italic", padding: "2em" }}>
        Lade E-Mail-Konfiguration…
      </p>
    );
  }

  // ── Edit mode ──────────────────────────────────────────────────────────────
  if (editMode) {
    return (
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        {/* Banner */}
        <div style={{
          display: "flex", alignItems: "center", justifyContent: "space-between",
          background: "#ebf8ff", border: "1px solid #90cdf4",
          borderRadius: "8px", padding: "0.75em 1.25em",
          marginBottom: "1.5em", gap: "1em",
        }}>
          <span style={{ color: "#2c5282", fontSize: "0.875rem", fontWeight: "500" }}>
            ✏️ Bearbeitungsmodus — Änderungen wirken sich auf die Live-E-Mails aus.
          </span>
          <button onClick={() => setEditMode(false)} style={{
            padding: "0.35em 0.9em", borderRadius: "6px",
            border: "1px solid #90cdf4", background: "#fff",
            color: "#2c5282", fontSize: "0.8rem", fontWeight: "600",
            cursor: "pointer",
          }}>
            ← Vorschau
          </button>
        </div>

        <div style={{
          background: "#fff", borderRadius: "10px",
          boxShadow: "0 1px 4px rgba(74,44,42,0.08)", padding: "2em",
        }}>
          <h3 style={{ margin: "0 0 1.5em", fontSize: "1rem", color: "#1a202c" }}>
            Versand-E-Mail bearbeiten
          </h3>

          {/* Global settings */}
          <div style={{
            background: "#fdf7f4", border: "1px solid #e8d8d0",
            borderRadius: "8px", padding: "1.1em 1.25em", marginBottom: "1.75em",
          }}>
            <p style={{
              margin: "0 0 0.75em", fontWeight: "700", fontSize: "0.85rem",
              color: "#4a2c2a", textTransform: "uppercase", letterSpacing: "0.04em",
            }}>
              Globale Einstellungen
            </p>

            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              Absender-Adresse
            </label>
            <div style={{ display: "flex", alignItems: "center",
              gap: "0.4em", marginBottom: "1em" }}>
              <input
                style={{ ...inputStyle, width: "160px", marginBottom: 0 }}
                value={form.fromLocal || ""}
                onChange={(e) => setField("fromLocal", e.target.value)}
                placeholder="versand"
              />
              <span style={{ color: "#4a5568", fontSize: "0.875rem",
                whiteSpace: "nowrap" }}>
                @{SHOP.email.split("@")[1]}
              </span>
            </div>

            <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
              E-Mail-Footer
            </label>
            <input
              style={{ ...inputStyle, marginBottom: 0 }}
              value={form.emailFooter || ""}
              onChange={(e) => setField("emailFooter", e.target.value)}
            />
          </div>

          {/* Subject */}
          <label style={labelStyle}>
            Betreff — Platzhalter: {"{{ORDER}}"}, {"{{TRACKING}}"}
          </label>
          <input
            style={inputStyle}
            value={form.subject || ""}
            onChange={(e) => setField("subject", e.target.value)}
          />

          {/* Body */}
          <label style={labelStyle}>
            Text — Platzhalter: {"{{NAME}}"}, {"{{ORDER}}"}, {"{{TRACKING}}"},
            {"{{TRACKING_LINK}}"}
          </label>
          <textarea
            style={{ ...taStyle, minHeight: "300px" }}
            value={form.body || ""}
            onChange={(e) => setField("body", e.target.value)}
          />

          <button onClick={handleSave} disabled={saving} style={{
            padding: "0.65em 2em", borderRadius: "7px",
            border: "none", background: saving ? "#a0aec0" : BRAND,
            color: "#fff", fontWeight: "700", fontSize: "0.95rem",
            cursor: saving ? "not-allowed" : "pointer",
          }}>
            {saving ? "Wird gespeichert…" : "Änderungen speichern"}
          </button>
        </div>
      </div>
    );
  }

  const handleSendTest = async () => {
    if (!testEmail) return;
    setTestSending(true);
    setTestResult(null);
    try {
      const fn = httpsCallable(
        getFunctions(undefined, "europe-west1"), "sendTestShippingEmail");
      await fn({
        to: testEmail,
        trackingNumber,
        orderId: orderNumber,
        name: firstName,
      });
      setTestResult("ok");
      setTimeout(() => setTestResult(null), 4000);
    } catch (err) {
      setTestResult("error");
      alert("Fehler beim Senden: " + err.message);
    } finally {
      setTestSending(false);
    }
  };

  // ── Preview mode ───────────────────────────────────────────────────────────
  const previewSubject = (form.subject || "")
    .replace(/\{\{ORDER\}\}/g, orderNumber)
    .replace(/\{\{TRACKING\}\}/g, trackingNumber);

  const previewHtml = buildShippingEmailHtml(
    form.body || "",
    firstName,
    orderNumber,
    trackingNumber,
    form.emailFooter,
  );

  return (
    <div style={{ maxWidth: 1100, margin: "0 auto" }}>
      {/* Header row */}
      <div style={{
        display: "flex", alignItems: "center",
        justifyContent: "space-between", marginBottom: "1em", gap: "1em",
      }}>
        <h2 style={{ margin: 0, fontSize: "1.25rem", color: "#1a202c" }}>
          Versand-E-Mail Vorschau
        </h2>
        <div style={{ display: "flex", alignItems: "center", gap: "0.75em" }}>
          {saved && (
            <span style={{ color: "#276749", fontWeight: "700", fontSize: "0.85rem" }}>
              ✓ Gespeichert!
            </span>
          )}
          <button onClick={() => setEditMode(true)} style={{
            padding: "0.45em 1.1em", borderRadius: "6px",
            border: "1px solid #e8d8d0", background: "#fff",
            color: "#4a5568", fontWeight: "600", fontSize: "0.85rem",
            cursor: "pointer",
          }}>
            ✏️ Text bearbeiten
          </button>
        </div>
      </div>

      {/* Sample data bar */}
      <div style={{
        background: "#fff", borderRadius: "10px",
        boxShadow: "0 1px 4px rgba(74,44,42,0.08)",
        padding: "1em 1.5em", marginBottom: "1.25em",
        display: "flex", gap: "1.25em", flexWrap: "wrap", alignItems: "flex-end",
      }}>
        <div>
          <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
            Vorschau-Name
          </label>
          <input
            value={firstName}
            onChange={(e) => setFirstName(e.target.value)}
            style={{ ...inputStyle, width: "140px", marginBottom: 0 }}
            placeholder="z.B. Maria"
          />
        </div>
        <div>
          <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
            Bestellnummer
          </label>
          <input
            value={orderNumber}
            onChange={(e) => setOrderNumber(e.target.value)}
            style={{ ...inputStyle, width: "150px", marginBottom: 0 }}
            placeholder="z.B. 4007861969"
          />
        </div>
        <div>
          <label style={{ ...labelStyle, marginBottom: "0.25em" }}>
            Tracking-Nummer
          </label>
          <input
            value={trackingNumber}
            onChange={(e) => setTrackingNumber(e.target.value)}
            style={{ ...inputStyle, width: "220px", marginBottom: 0 }}
            placeholder="z.B. 00340434334442708607"
          />
        </div>
      </div>

      {/* Test email sender */}
      <div style={{
        background: "#fff", borderRadius: "10px",
        boxShadow: "0 1px 4px rgba(74,44,42,0.08)",
        padding: "0.9em 1.5em", marginBottom: "1.25em",
        display: "flex", gap: "0.75em", alignItems: "center", flexWrap: "wrap",
      }}>
        <span style={{ fontSize: "0.8rem", fontWeight: "600", color: "#4a5568",
          textTransform: "uppercase", letterSpacing: "0.04em", whiteSpace: "nowrap" }}>
          Test-E-Mail senden
        </span>
        <input
          type="email"
          value={testEmail}
          onChange={(e) => setTestEmail(e.target.value)}
          placeholder="test@example.com"
          style={{
            flex: 1, minWidth: "200px", padding: "0.4em 0.75em",
            border: "1px solid #e8d8d0", borderRadius: "6px",
            fontSize: "0.875rem", color: "#2d3748",
          }}
        />
        <button
          onClick={handleSendTest}
          disabled={testSending || !testEmail}
          style={{
            padding: "0.4em 1.1em", borderRadius: "6px", border: "none",
            background: testSending || !testEmail ? "#e2e8f0" : BRAND,
            color: testSending || !testEmail ? "#a0aec0" : "#fff",
            fontWeight: "600", fontSize: "0.875rem",
            cursor: testSending || !testEmail ? "default" : "pointer",
            whiteSpace: "nowrap",
          }}
        >
          {testSending ? "Sende…" : "Jetzt senden"}
        </button>
        {testResult === "ok" && (
          <span style={{ color: "#276749", fontWeight: "700", fontSize: "0.85rem" }}>
            ✓ Gesendet!
          </span>
        )}
      </div>

      {/* Subject line */}
      <div style={{
        background: "#fff", borderRadius: "8px 8px 0 0",
        border: "1px solid #e8d8d0", borderBottom: "none",
        padding: "0.75em 1.25em", fontSize: "0.875rem", color: "#4a5568",
      }}>
        <span style={{ fontWeight: "600", color: "#2d3748" }}>Betreff: </span>
        {previewSubject}
      </div>

      {/* Email iframe */}
      <iframe
        key={`${firstName}-${orderNumber}-${trackingNumber}-${JSON.stringify(form.body)}`}
        srcDoc={previewHtml}
        sandbox="allow-same-origin"
        style={{
          width: "100%", height: "680px",
          border: "1px solid #e8d8d0", borderRadius: "0 0 8px 8px",
          boxShadow: "0 1px 4px rgba(74,44,42,0.08)", display: "block",
        }}
        title="Versand-E-Mail Vorschau"
      />
    </div>
  );
};

export default ShippingEmailPreview;
